import { useEffect, useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import Head from 'components/Header/Head';
import Header from 'components/Header/Header';
import ArticleNav from 'components/Post/Article/ArticleNav';
import ArticleBody from 'components/Post/Article/ArticleBody';
import RouteFlourish from 'components/Post/Article/RouteFlourish';
import Footer from 'components/General/Footer';
import { getUser } from 'helpers/token';
import Image from 'components/FutureImage';
import { fraunces } from 'components/Landing/fonts';
import {
  ArrowIcon,
  CtaButton,
  Eyebrow,
  GhostButton,
  INK,
  MUTED,
  PAPER_CARD,
} from 'components/Landing/ui';
import cover from '../../public/img/leaving-wordpress/cover.jpg';
import wandereinShot from '../../public/img/blog-examples/wanderein.jpg';
import articleShot from '../../public/img/blog-platforms/travelfeed-article.jpg';

// Signup CTAs open the boarding-pass JoinDialog (via LoginButton), never
// the legacy /join page; lazy so the dialog stack stays out of the bundle.
const LoginButton = dynamic(
  () => import('components/Header/LoginButton'),
  { ssr: false },
);

const CANONICAL = 'https://travelfeed.com/leaving-wordpress';
const COVER_URL = 'https://travelfeed.com/img/leaving-wordpress/cover.jpg';

// Rendered as visible FAQ AND FAQPage JSON-LD — keep both from one array.
const FAQ = [
  {
    questionName: 'Can I import my WordPress posts into TravelFeed?',
    acceptedAnswerText:
      'Yes — posts, drafts and pages, imported for free. Export your content from the WordPress dashboard, upload it to TravelFeed, and your archive comes across with its formatting. Your images arrive still pointing at your old server; once a post is published here we keep a permanent copy of every image in it and serve that copy from then on, so you can cancel the old hosting without old posts going blank. Comments are the one thing that stays behind. If anything about the move needs a human, our team helps at no additional cost; migration help is included with every plan, not sold as an extra.',
  },
  {
    questionName:
      'What can TravelFeed do that a WordPress travel theme cannot?',
    acceptedAnswerText:
      'The travel structure builds itself. Pin a post to a place and TravelFeed generates the country, region and city pages for it, linked to each other and ready for search. On WordPress that is categories and menus you groom by hand for the life of the blog. Maps, photo galleries and day-by-day itineraries are blocks in the editor rather than plugins to research, buy and keep compatible, and every post gets share cards made for it, including the vertical pin Pinterest wants. Then there is the part a theme cannot copy: your stories also publish into the TravelFeed community, so a blog that is one week old already has readers.',
  },
  {
    questionName: 'Will my Google rankings survive the move?',
    acceptedAnswerText:
      "Honestly: moves are survivable but not free. If you keep your domain — which every TravelFeed hosting plan supports — your URLs can keep working and most authority carries over, though Google typically takes some weeks to recrawl and rankings can wobble during reindexing. What we won't do is promise a traffic guarantee; nobody honest can. What helps most: keep the domain, keep post URLs stable or redirected, and move when you have a quiet month.",
  },
  {
    questionName: 'What does TravelFeed cost compared to WordPress hosting?',
    acceptedAnswerText:
      'Comparable money, radically different labor. A TravelFeed blog on your own domain is $150 in year one on Pro (a 50% new-member discount applies automatically at checkout; it renews at $300/yr) or $180/yr on Basic — hosting, SSL, CDN, image optimization and all maintenance included. Realistic WordPress hosting runs $150–250 a year after intro pricing expires, plus a theme, the occasional paid plugin, and the hours you spend being your own webmaster.',
  },
  {
    questionName: 'Do I keep my domain?',
    acceptedAnswerText:
      "Yes. Your domain is registered with your registrar and stays yours — you point it at TravelFeed in the hosting dashboard, a copy-paste job with step-by-step instructions. Years of bookmarks, backlinks and business cards keep working. If you ever leave, the domain leaves with you; that's the point of owning one.",
  },
];

// Origin-story lander of the blogger-intent cluster (docs/seo-plan.md §3):
// the page exists because of one WordPress afternoon. Framing of the story
// is SETTLED per seo-plan §5 — do not re-soften or escalate it.
export default function LeavingWordpress() {
  const [isMember, setIsMember] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  useEffect(() => {
    if (getUser()) setIsMember(true);
  }, []);

  useEffect(() => {
    document.body.classList.add('tf-paper');
    return () => document.body.classList.remove('tf-paper');
  }, []);

  return (
    <>
      <Head
        shorttitle="Leaving WordPress: An Easier Home for Your Travel Blog"
        description="A WordPress alternative for travel bloggers: free import of your posts, your domain comes with you, and nobody has to be their own webmaster. Honest about when to stay, too."
        seo={{ canonicalUrl: CANONICAL }}
        image={`https://og.travelfeed.com/api/post?title=${encodeURIComponent(
          'Leaving WordPress: An Easier Home for Your Travel Blog',
        )}&image=${encodeURIComponent(COVER_URL)}`}
        post={{
          author: { username: 'jpphotography', displayName: 'Julian' },
          createdAt: '2026-07-16',
          updatedAt: '2026-07-16',
          tags: ['travel-blogging'],
          thumbnail: COVER_URL,
        }}
        faq={FAQ}
      />
      {isMember ? <Header /> : <ArticleNav />}
      <div className="tw-pt-20" />

      <header className="tw-mx-auto tw-max-w-[46rem] tw-px-4 tw-pb-4 tw-pt-8 sm:tw-pt-12">
        <Eyebrow>The exit, honestly mapped</Eyebrow>
        <h1
          className={`${fraunces.className} tw-m-0 tw-text-4xl tw-font-semibold tw-leading-[1.08] tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] sm:tw-text-5xl`}
        >
          The WordPress alternative for travel bloggers who&rsquo;d rather
          write
        </h1>
        <p className={`${MUTED} tw-mb-0 tw-mt-5 tw-text-lg tw-leading-relaxed`}>
          If your travel blog runs on WordPress and some part of you dreads
          opening the admin panel, this page is for you. Your posts import
          for free, your domain comes with you, and the maintenance becomes
          our job. We&rsquo;ll also tell you when staying is the right call.
        </p>
        <p className="tw-mb-0 tw-mt-5 tw-font-mono tw-text-[11px] tw-uppercase tw-tracking-[0.18em] tw-text-[#8A958F]">
          By{' '}
          <Link
            href="/@jpphotography"
            className="tw-text-primary-700 dark:tw-text-primary-300 hover:tw-underline"
          >
            Julian
          </Link>
          , founder of TravelFeed · Updated July 2026
        </p>
      </header>

      <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-pb-6 tw-pt-4">
        <div className="tw-rounded-[4px] tw-bg-white dark:tw-bg-[#EFEAE0] tw-p-3 tw-shadow-[0_30px_60px_-20px_rgba(29,45,40,0.35)]">
          <Image
            src={cover}
            alt="Painted moving-day scene: packed boxes and a potted plant beside a desk, a laptop showing a calm travel blog page"
            priority
            sizes="(max-width: 1024px) 92vw, 990px"
            className="tw-h-auto tw-w-full tw-rounded-[2px]"
          />
        </div>
      </div>

      <ArticleBody>
        <p>
          Nobody googles for a WordPress alternative on a good day. You get
          here after an update broke the theme, or the renewal invoice
          tripled, or a Saturday disappeared into a plugin conflict — some
          afternoon when running the blog took the place of writing it. We
          know the feeling unusually well, because an afternoon exactly like
          that is why TravelFeed hosting exists.
        </p>

        <aside
          className={`${PAPER_CARD} tw-my-10 tw-rounded-2xl tw-border-dashed tw-p-6 sm:tw-p-8 [border-width:1.5px]`}
        >
          <p className="tw-mb-2 tw-mt-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.22em] tw-text-[#8A958F]">
            The afternoon in question
          </p>
          <p className={`${INK} tw-mb-0 tw-text-[0.95em] tw-leading-relaxed`}>
            TravelFeed started in 2018 as a community of travel writers; the
            hosting came later, and not from a business plan. A plugin
            update broke my girlfriend&rsquo;s never-fast WordPress travel
            blog. She asked her host — Bluehost, at the time — for help, and
            by the end of the support session the site was past saving. That
            was one experience, years ago, and plenty of blogs live there
            happily — but it settled the design brief for everything we
            built afterwards: the person doing the writing should never also
            be the person doing the server rescue.
          </p>
        </aside>

        <h2 id="what-breaks-eventually">What breaks, eventually</h2>
        <p>
          Let&rsquo;s be fair to WordPress first: the software is free,
          genuinely excellent, and runs a huge share of the web. Nothing
          below is a quality complaint. It&rsquo;s a labor complaint — with
          self-hosted WordPress, you are the webmaster, and the job has a
          way of growing:
        </p>
        <ul>
          <li>
            <strong>The update treadmill.</strong> Core, theme and every
            plugin update on its own schedule, written by different authors
            who never tested against each other. Most updates are fine.
            The one that isn&rsquo;t picks its own timing — ask our
            founding afternoon.
          </li>
          <li>
            <strong>The renewal ladder.</strong> The $2.99/month that got
            you in was an intro rate on a multi-year prepay; renewals land
            at two to five times that, and realistic long-term hosting runs
            $150–250 a year before themes and paid plugins. Cheap shared
            hosting has a further cost: a slow, photo-heavy blog that
            readers abandon and Google ranks lower.
          </li>
          <li>
            <strong>Backups you&rsquo;ll meet only once.</strong> They
            matter on exactly one day, and on that day they&rsquo;re either
            recent, complete and restorable — or they&rsquo;re a lesson.
            Arranging that is also your job.
          </li>
          <li>
            <strong>Travel features by assembly.</strong> Post maps,
            galleries, itineraries: each one a plugin you research, buy,
            configure and keep compatible. And the structure a travel blog
            lives on, a page per country, per region, per city, all linked to
            each other, is categories and menus you groom by hand for as long
            as you keep publishing. The blog becomes a stack you administer.
          </li>
        </ul>
        <p>
          If you read that list and felt at home — genuinely, stay. We wrote
          a whole section below about who should. But if you read it and
          felt the Saturday slipping away, the exit is shorter than you
          think.
        </p>

        <h2 id="what-actually-moves">The move: everything comes with you</h2>
        <p>
          Migrating sounds heavier than it is. Three things move, and all
          three are covered:
        </p>
        <ul>
          <li>
            <strong>Your content — imported free.</strong> Posts, drafts and
            pages come across from a standard WordPress export with their
            formatting. Your images arrive still pointing at your old server,
            and once you publish a post here we take a permanent copy of
            every image in it, so you can retire that hosting without your
            archive going blank. Comments are the one thing that stays
            behind. The walkthrough is in our{' '}
            <Link href="/help/article/migrating-from-wordpress">
              WordPress migration guide
            </Link>
            , and this is exactly the part where a human helps, at no
            additional cost. Tell us before you switch.
          </li>
          <li>
            <strong>Your domain — still yours.</strong> It stays at your
            registrar; you point it at TravelFeed with copy-paste
            instructions from the hosting dashboard. Bookmarks, backlinks
            and years of Google history keep aiming at the same address.
          </li>
          <li>
            <strong>Your habits — minus the admin panel.</strong> You write
            in a block editor, photos become galleries, posts pin to a map.
            That pin is also what builds your country, region and city pages,
            linked to each other, without you touching a menu. Updates,
            security, backups, SSL, CDN and image optimization are our
            problem, permanently. There is no plugin aisle because
            there&rsquo;s nothing to fix with one.
          </li>
        </ul>
        <p>
          The honest print: a big move is never entirely free of friction —
          Google takes some weeks to recrawl, and rankings can wobble while
          it does (more in the FAQ). What you get for it is a blog that
          stays fast without a caching plugin, ships{' '}
          <Link href="/blog/wordpress-vs-travelfeed-the-best-choice-for-travel-bloggers-2023">
            SEO basics as defaults instead of plugins
          </Link>
          , sorts every story by place so your destination pages grow on
          their own, publishes into a travel community that reads and
          rewards from your first post, and includes AI writing credits in
          every plan.
        </p>
        <p>
          Here&rsquo;s what the other side looks like. Rein moved her travel
          blog from WordPress after it became, in her words, unusably slow —
          today it lives on her own domain, on our hosting:
        </p>
        <figure className="tf-wide">
          <Image
            src={wandereinShot}
            alt="wanderein.com — a travel blog that moved from WordPress to TravelFeed hosting, on its own custom domain"
            sizes="(max-width: 640px) 100vw, (max-width: 1088px) 92vw, 1024px"
            className="tw-h-auto tw-w-full"
          />
        </figure>
        <p>
          Her blog is one of{' '}
          <Link href="/blog/travel-blog-examples">
            four real member blogs we&rsquo;ve profiled
          </Link>
          . And this is what a single story looks like here — a real page at
          a real address, no theme shopping required:
        </p>
        <figure className="tf-wide">
          <Image
            src={articleShot}
            alt="A live TravelFeed article with a full-width photo masthead, GPS coordinates and editorial typography"
            sizes="(max-width: 640px) 100vw, (max-width: 1088px) 92vw, 1024px"
            className="tw-h-auto tw-w-full"
          />
        </figure>

        <h2 id="what-it-costs">The money, stated plainly</h2>
        <p>
          Comparable invoice, different labor. A TravelFeed blog on your own
          domain is <strong>$150 in year one on Pro</strong> — a 50%
          new-member discount applies automatically at checkout — renewing
          at $300/yr, or $180/yr on Basic; both billed monthly if you
          prefer, with no long-term lock-in. Realistic WordPress hosting
          runs $150–250 a year once intro pricing expires, plus theme,
          plugins, and the hours. The line we&rsquo;d put on the invoice if
          we could: on one side of this comparison, the webmaster is you;
          on the other, it&rsquo;s us. Where the ten-platform field sits
          price-wise is in{' '}
          <Link href="/blog/best-travel-blog-platforms">
            the full platform comparison
          </Link>
          .
        </p>

        <h2 id="when-to-stay">When you should stay on WordPress</h2>
        <p>
          The honest section, as promised. Keep WordPress with our blessing
          if:
        </p>
        <ul>
          <li>
            your site is more than a travel blog — a shop, client work, a
            business with custom needs;
          </li>
          <li>
            you rely on specific plugins or custom code — we don&rsquo;t run
            either, and customization here stays within our designs;
          </li>
          <li>
            you genuinely enjoy the tinkering — WordPress rewards that like
            nothing else, and the maintenance list above reads as a hobby,
            not homework, to some people;
          </li>
          <li>
            your traffic already funds a maintenance plan or a developer —
            at that scale the labor argument weakens.
          </li>
        </ul>
        <p>
          Still deciding rather than leaving? The detailed head-to-head is{' '}
          <Link href="/blog/wordpress-vs-travelfeed-the-best-choice-for-travel-bloggers-2023">
            WordPress vs. TravelFeed
          </Link>
          , and if you&rsquo;re earlier than that — no blog yet anywhere —
          start with{' '}
          <Link href="/start-a-travel-blog">
            how to start a travel blog
          </Link>
          .
        </p>

        <h2 id="faq">Frequently asked questions</h2>
        {FAQ.map(entry => (
          <div key={entry.questionName}>
            <h3>{entry.questionName}</h3>
            <p>{entry.acceptedAnswerText}</p>
          </div>
        ))}
      </ArticleBody>

      <section className="tw-mx-auto tw-max-w-[46rem] tw-px-4 tw-pb-16 tw-pt-8">
        <RouteFlourish className="tw-pb-10" />
        <div
          className={`${PAPER_CARD} tw-rounded-2xl tw-p-7 tw-text-center sm:tw-p-10`}
        >
          <p
            className={`${fraunces.className} ${INK} tw-mb-3 tw-mt-0 tw-text-2xl tw-font-semibold tw-tracking-tight sm:tw-text-3xl`}
          >
            Bring the blog. Leave the maintenance.
          </p>
          <p className={`${MUTED} tw-mx-auto tw-mb-7 tw-mt-0 tw-max-w-lg`}>
            Free import of your posts, your domain comes with you, and the
            last plugin update of your life is the one you don&rsquo;t
            install.
          </p>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3">
            <CtaButton onClick={() => setJoinOpen(true)}>
              Start the move
              <ArrowIcon />
            </CtaButton>
            <GhostButton href="/help/article/migrating-from-wordpress">
              Read the migration guide first
            </GhostButton>
          </div>
        </div>
      </section>

      {joinOpen && (
        <LoginButton
          open
          hideButtons
          onClickClose={() => setJoinOpen(false)}
        />
      )}
      <Footer isNoSpace />
    </>
  );
}
